// Import default theme CSS global tokens to be able to extract the default configuration from
// document style object
import { camelCase, omit } from 'lodash';
import { ThemeConfig } from 'antd/es/config-provider';
import { CssVariablesCamelizeString } from '@commutifi-fe/interfaces';
import defaultTheme, { Styles as DefaultTheme } from '../style/themes/default.module.scss';
import { spacing } from '../style/constants';
export type CommutifiToken = Record<CssVariablesCamelizeString<keyof DefaultTheme>, string>;
export const extractThemeFromDocumentStyle = () => {
  const styles = getComputedStyle(document.body);
  // Here we use the default theme simply because we know it has ALL the style keys
  return Object.entries(defaultTheme).reduce((acc, [key, value]) => ({
    ...acc,
    [camelCase(key)]: `rgb(${styles.getPropertyValue(key) || value})`
  }), {} as CommutifiToken);
};
const dynamicColors = extractThemeFromDocumentStyle();
export const defaultConfig: ThemeConfig = {
  token: {
    // Let antd algorithm generate proper background colors, it's quite good at it
    ...omit(dynamicColors, ['colorBgContainer', 'colorBgElevated', 'colorBgLayout', 'backgroundColor']),
    colorPrimary: dynamicColors.primary400 || '',
    colorLink: dynamicColors.primary400 || '',
    colorLinkHover: dynamicColors.primary500 || '',
    colorLinkActive: dynamicColors.primary500 || '',
    colorSuccess: dynamicColors.feedbackSuccess400,
    colorWarning: dynamicColors.feedbackWarning400,
    colorError: dynamicColors.feedbackDestructive500,
    colorIcon: dynamicColors.neutral600 || '',
    colorText: dynamicColors.neutral600 || '',
    colorTextDisabled: dynamicColors.neutral500 || '',
    colorTextHeading: dynamicColors.neutral600 || '',
    colorTextPlaceholder: dynamicColors.neutral500 || '',
    colorTextSecondary: dynamicColors.neutral500 || '',
    borderRadius: 4,
    fontFamily: 'Segma',
    colorPrimaryActive: dynamicColors.primary500 || '',
    colorPrimaryHover: dynamicColors.primary500 || '',
    colorErrorHover: dynamicColors.feedbackDestructive500,
    fontSizeSM: 12,
    fontSize: 14,
    fontSizeLG: 16,
    lineHeight: 1.5714,
    lineHeightSM: 1.5,
    lineHeightLG: 1.625,
    controlHeightSM: 32,
    controlHeight: 40,
    // WCAG AA: Set loading opacity to 1 so a11y tools can calculate contrast.
    // Visual distinction is achieved via brightness filter in AppButton styles.
    opacityLoading: 1
  },
  components: {
    Alert: {
      colorInfoBorder: 'transparent',
      colorWarningBorder: 'transparent',
      colorSuccessBorder: 'transparent'
    },
    Button: {
      borderRadius: 6,
      borderRadiusSM: 6,
      colorPrimaryBorder: 'unset',
      colorBgTextHover: 'transparent',
      colorLinkHover: dynamicColors.primary600 || '',
      fontWeight: 700,
      primaryShadow: `0px 4px 8px -4px rgba(0, 132, 130, 0.48)`,
      ghostBg: 'transparent',
      dangerShadow: `0px 2px 4px -2px rgba(5, 26, 21, 0.12)`,
      paddingBlockSM: spacing.tiny,
      paddingInlineSM: `calc(${spacing.medium} - 1px)`,
      linkFocusDecoration: 'underline'
    },
    Checkbox: {
      colorPrimary: dynamicColors.primary350 || '',
      colorPrimaryHover: dynamicColors.primary400 || '',
      controlInteractiveSize: 16
    },
    Divider: {
      colorSplit: dynamicColors.neutral300 || '',
      fontSizeLG: 14,
      colorBgTextHover: 'transparent'
    },
    InputNumber: {
      controlOutlineWidth: 1,
      colorErrorOutline: dynamicColors.feedbackDestructive500
    },
    Menu: {
      activeBarBorderWidth: 2
    },
    Message: {
      colorBgElevated: dynamicColors.colorBgLayout || '',
      boxShadow: '0px 12px 24px -10px rgba(146, 148, 152, 0.301054)',
      borderRadiusLG: 6
    },
    Notification: {
      borderRadius: 6
    },
    Radio: {
      colorPrimary: dynamicColors.primary350 || ''
    },
    Select: {
      controlOutlineWidth: 1
    },
    Switch: {
      colorPrimary: dynamicColors.primary350 || '',
      colorPrimaryHover: dynamicColors.primary400 || '',
      controlHeight: 32
    },
    Tag: {},
    Timeline: {
      lineType: 'dashed'
    },
    Tree: {
      colorPrimary: dynamicColors.primary350 || '',
      colorPrimaryHover: dynamicColors.primary400 || '',
      controlInteractiveSize: 16
    }
  }
};